import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  getPortalMetricsSeries,
  getPortalMetricLabelOptions,
} from '@/services/portal.service'
import { portalKeys } from '@/hooks/use-portal'
import {
  CARD_ORDER,
  DEFAULT_FINANCIAL_METRIC_CARD_LABELS,
  EMPTY_FINANCIAL_METRIC_CARD_SELECTION,
  type CompanyMetricSeries,
  type ExpandedPeriodType,
  type FinancialMetricCardKey,
  type FinancialMetricCardSelection,
  type FinancialMetricOption,
} from '@/hooks/use-metrics'
import type {
  CompanyMetricSeriesItemDto,
  CompanyMetricsView,
} from '@/services/metrics.service'

function toMetricOptionLabel(
  labeledAs: string,
  metricTypeName?: string
): string {
  if (labeledAs === 'Adjusted EBITDA') return 'Adj. EBITDA'
  return metricTypeName ?? labeledAs
}

function dedupeMetricLabelIds(ids: string[]): string[] {
  const seen = new Set<string>()
  const deduped: string[] = []
  for (const id of ids) {
    if (seen.has(id)) continue
    seen.add(id)
    deduped.push(id)
  }
  return deduped
}

function toSeriesMapById(
  series: CompanyMetricSeriesItemDto[]
): Map<string, CompanyMetricSeries> {
  return new Map(
    series.map((item) => [
      item.metricLabelId,
      {
        metricLabelId: item.metricLabelId,
        labeledAs: item.labeledAs,
        latestValue: item.latestValue,
        comparisonValue: item.comparisonValue,
        changePercent: item.changePercent,
        changeDisplay: item.changeDisplay,
        comparisonQuarter: item.comparisonQuarter ?? null,
        points: item.points,
      },
    ])
  )
}

function toSeriesMapByLabel(
  series: CompanyMetricSeriesItemDto[]
): Map<string, CompanyMetricSeries> {
  const map = new Map<string, CompanyMetricSeries>()
  for (const item of series) {
    if (map.has(item.labeledAs)) continue
    map.set(item.labeledAs, {
      metricLabelId: item.metricLabelId,
      labeledAs: item.labeledAs,
      latestValue: item.latestValue,
      comparisonValue: item.comparisonValue,
      changePercent: item.changePercent,
      changeDisplay: item.changeDisplay,
      comparisonQuarter: item.comparisonQuarter ?? null,
      points: item.points,
    })
  }
  return map
}

export function usePortalMetricCards(
  selection: FinancialMetricCardSelection = EMPTY_FINANCIAL_METRIC_CARD_SELECTION
) {
  const selectedMetricLabelIds = useMemo(() => {
    const selectedIds = CARD_ORDER.map((key) => selection[key]).filter(
      (id): id is string => Boolean(id)
    )
    return dedupeMetricLabelIds(selectedIds).sort()
  }, [selection])

  const defaultQuery = useQuery({
    queryKey: portalKeys.metricsSeries({
      mode: 'defaults',
      periodType: 'LTM',
      scenarioType: 'ACTUAL',
      quarters: 4,
      changeBasis: 'YOY',
    }),
    queryFn: () =>
      getPortalMetricsSeries({
        periodType: 'LTM',
        scenarioType: 'ACTUAL',
        quarters: 4,
        changeBasis: 'YOY',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const customQuery = useQuery({
    queryKey: portalKeys.metricsSeries({
      mode: 'custom',
      metricLabelIds: selectedMetricLabelIds,
      periodType: 'LTM',
      scenarioType: 'ACTUAL',
      quarters: 4,
      changeBasis: 'YOY',
    }),
    queryFn: () =>
      getPortalMetricsSeries({
        metricLabelIds:
          selectedMetricLabelIds.length > 0
            ? selectedMetricLabelIds
            : undefined,
        periodType: 'LTM',
        scenarioType: 'ACTUAL',
        quarters: 4,
        changeBasis: 'YOY',
      }),
    enabled: selectedMetricLabelIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })

  const defaultSeries = defaultQuery.data?.data ?? []
  const customSeries = customQuery.data?.data ?? []
  const defaultByLabel = toSeriesMapByLabel(defaultSeries)
  const customById = toSeriesMapById(customSeries)
  const customQueryEnabled = selectedMetricLabelIds.length > 0

  const cards: Partial<Record<FinancialMetricCardKey, CompanyMetricSeries>> = {}
  const status: Record<
    FinancialMetricCardKey,
    { isLoading: boolean; isError: boolean }
  > = {
    revenue: { isLoading: false, isError: false },
    grossProfit: { isLoading: false, isError: false },
    ebitda: { isLoading: false, isError: false },
    cash: { isLoading: false, isError: false },
    netIncome: { isLoading: false, isError: false },
  }

  const customQueryResolved = customQueryEnabled && !customQuery.isLoading

  CARD_ORDER.forEach((key) => {
    const defaultLabel = DEFAULT_FINANCIAL_METRIC_CARD_LABELS[key]
    const selectedMetricLabelId = selection[key]
    const seriesForCard = selectedMetricLabelId
      ? (customById.get(selectedMetricLabelId) ??
        (customQueryResolved ? undefined : defaultByLabel.get(defaultLabel)))
      : defaultByLabel.get(defaultLabel)

    if (seriesForCard) {
      cards[key] = seriesForCard
    }

    const isDefaultCardLoading =
      defaultQuery.isLoading && !defaultByLabel.get(defaultLabel)
    const isSelectedCardLoading = selectedMetricLabelId
      ? customQueryEnabled &&
        customQuery.isFetching &&
        !customById.get(selectedMetricLabelId)
      : false

    status[key] = {
      isLoading: isDefaultCardLoading || isSelectedCardLoading,
      isError:
        defaultQuery.isError ||
        (Boolean(selectedMetricLabelId) &&
          customQueryEnabled &&
          customQuery.isError),
    }
  })

  return {
    cards,
    status,
    isError:
      defaultQuery.isError || (customQueryEnabled && customQuery.isError),
  }
}

export function usePortalMetricLabelOptions(): {
  data: FinancialMetricOption[] | undefined
} {
  return useQuery({
    queryKey: portalKeys.metricLabels(),
    queryFn: () =>
      // No periodType filter: balance-sheet labels only exist as point-in-time
      // (QUARTERLY/MONTHLY) rows, so filtering by LTM would hide them entirely.
      // The picker should expose every label that has data; the chart's period
      // selector then governs which periodType is queried for the series.
      getPortalMetricLabelOptions({ scenarioType: 'ACTUAL' }),
    staleTime: 5 * 60 * 1000,
    select: (response) =>
      (response.data ?? []).map((opt) => ({
        id: opt.id,
        labeledAs: opt.labeledAs,
        label: toMetricOptionLabel(opt.labeledAs, opt.metricTypeName),
        category: opt.metricTypeCategory,
        sectionName: opt.globalSectionName,
        sortOrder: opt.sortOrder ?? null,
      })),
  })
}

const EXPANDED_PERIOD_PARAMS: Record<
  ExpandedPeriodType,
  { periodType: ExpandedPeriodType; quarters: number; months?: number }
> = {
  MONTHLY: { periodType: 'MONTHLY', quarters: 8, months: 24 },
  QUARTERLY: { periodType: 'QUARTERLY', quarters: 8 },
  LTM: { periodType: 'LTM', quarters: 8 },
  YTD: { periodType: 'YTD', quarters: 12 },
}

const EXPANDED_VIEW_PARAMS: Record<
  CompanyMetricsView,
  { quarters?: number; months?: number; years?: number }
> = {
  MONTHLY: { months: 24 },
  QUARTERLY: { quarters: 8 },
  ANNUAL: { years: 5 },
}

export function usePortalExpandedMetricSeries(
  metricLabelId: string | undefined,
  periodType: ExpandedPeriodType = 'LTM',
  enabled: boolean = false,
  view?: CompanyMetricsView
) {
  const periodParams = EXPANDED_PERIOD_PARAMS[periodType]
  const viewParams = view ? EXPANDED_VIEW_PARAMS[view] : undefined

  // When view is set, the column cadence is decoupled from periodType: counts
  // come from the view, not the period (matching the financials table).
  const quarters = viewParams ? viewParams.quarters : periodParams.quarters
  const months = viewParams ? viewParams.months : periodParams.months
  const years = viewParams?.years

  const query = useQuery({
    queryKey: portalKeys.metricsSeries({
      mode: 'expanded',
      metricLabelId,
      periodType: periodParams.periodType,
      quarters,
      months,
      years,
      view,
    }),
    queryFn: () =>
      getPortalMetricsSeries({
        metricLabelIds: metricLabelId ? [metricLabelId] : undefined,
        periodType: periodParams.periodType,
        scenarioType: 'ACTUAL',
        quarters,
        months,
        years,
        view,
        changeBasis: 'YOY',
      }),
    enabled: enabled && Boolean(metricLabelId),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })

  const series = useMemo(() => {
    const data = query.data?.data ?? []
    if (!metricLabelId) return undefined
    return data.find((item) => item.metricLabelId === metricLabelId)
  }, [query.data, metricLabelId])

  return useMemo(
    () => ({
      series: series
        ? {
            metricLabelId: series.metricLabelId,
            labeledAs: series.labeledAs,
            latestValue: series.latestValue,
            comparisonValue: series.comparisonValue,
            changePercent: series.changePercent,
            changeDisplay: series.changeDisplay,
            comparisonQuarter: series.comparisonQuarter ?? null,
            points: series.points,
          }
        : undefined,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isError: query.isError,
    }),
    [series, query.isLoading, query.isFetching, query.isError]
  )
}
